import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import type { SelectOption } from '@scaleaq/scaleui';

type UseSelectEntityControlParams<T extends { id?: string }> = {
  options: SelectOption<T>[];
  /** Initial selection when the control mounts. */
  initialSelected?: T;
  /**
   * Called after the user picks an option (before state commits). Receives the new value and
   * the previous `selected` from the last render.
   */
  onSelectionChange?: (next: T | undefined, previous: T | undefined) => void;
};

type useSelectEntityControlResult<T> = {
  selected: T | undefined;
  setSelected: Dispatch<SetStateAction<T | undefined>>;
  value: SelectOption<T> | undefined;
  onChange: (selectedOption: SelectOption<T> | null) => void;
};

/**
 * Keeps selected entity state plus ScaleUI/react-select `value` / `onChange`, with refetch-safe
 * option matching. Build `options` outside the hook.
 */
export const useSelectEntityControl = <T extends { id?: string }>({
  options,
  initialSelected,
  onSelectionChange,
}: UseSelectEntityControlParams<T>): useSelectEntityControlResult<T> => {
  const [selected, setSelected] = useState<T | undefined>(initialSelected);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const value = findSelectOptionMatchingEntity(options, selected);

  const onChange = useCallback((selectedOption: SelectOption<T> | null) => {
    const next = selectedOption?.value;
    const previous = selectedRef.current;
    onSelectionChangeRef.current?.(next, previous);
    setSelected(next);
  }, []);

  return { selected, setSelected, value, onChange };
};

//*****************
// Helper Function
//*****************
/** Resolves the option row for react-select when `selected` may be a stale instance after refetch. */
function findSelectOptionMatchingEntity<T extends { id?: string }>(
  options: SelectOption<T>[],
  selected: T | undefined
): SelectOption<T> | undefined {
  if (!selected) {
    return undefined;
  }
  return options.find(
    o =>
      o.value === selected ||
      (selected.id != null && o.value.id != null && o.value.id === selected.id)
  );
}
